import { useMemo, useCallback, useContext, useState, useRef } from "react";
import { shuffle } from "lodash";

import { CartContext } from "@context/CartProvider";
import useDiscountsInfo from "@hooks/useDiscountsInfo";
import { useTracking } from "@context/Tracking";
import useManyCheckInventory from "@hooks/useManyCheckInventory";

import FreeGiftProduct from "@components/sideCart/freeGiftSelection/FreeGiftProduct";
import DISCOUNT_TYPES from "@constants/DiscountTypes";
import { ALButtonV2 } from "@components/ALComponents";

import CloseIcon from "@svgs/close-delete.svg";

function FreeGiftSelection({ lines, closeGiftSelection }) {
  const giftInCart = lines.find((line) =>
    line.attributes.find(({ key, value }) => key === "_free_gift" && value === "true")
  );

  const [selectedGift, setSelectedGift] = useState(
    () => giftInCart?.variant?.product?.handle || null
  );

  const { addManyProductsToCart } = useContext(CartContext);
  const { trackAddFreeGift } = useTracking();
  const discountsInfo = useDiscountsInfo();

  const giftDiscount =
    (discountsInfo || []).find((s) => s.type === DISCOUNT_TYPES.GIFT_WITH_PURCHASE) || null;
  const freeGiftProducts = useMemo(() => giftDiscount?.freegift?.products || [], [giftDiscount]);
  const freeGiftProductsInventory = useManyCheckInventory(freeGiftProducts);

  const giftProductsRandomized = useRef(shuffle(freeGiftProducts));

  const handleConfirmationClick = useCallback(() => {
    const product = freeGiftProducts.find((p) => p.handle === selectedGift);

    if (giftInCart && giftInCart?.variant?.product?.handle === product?.shopify?.handle) {
      // If the selected gift is the same as the one already in the cart, do nothing
      closeGiftSelection();
      return;
    }

    const {
      shopify: { shopifyId, title, handle, productType, images, variants },
    } = product;
    const dataProductsToAdd = [
      {
        variant: {
          id: variants[0].id,
          sku: variants[0].sku,
          product: {
            title,
            id: shopifyId,
            handle,
            productType,
            images,
          },
        },
        quantity: 1,
        attributes: [{ key: "_free_gift", value: "true" }],
      },
    ];
    // Remove previous gift if different from the selected one
    if (giftInCart?.variant?.product?.handle !== selectedGift) {
      dataProductsToAdd.push({
        ...giftInCart,
        quantity: 0,
      });
    }
    trackAddFreeGift({
      productId: product.shopify.shopifyId,
      productTitle: product.shopify.title,
      productHandle: product.handle,
      type: "swap",
    });
    addManyProductsToCart({ dataProductsToAdd });
    closeGiftSelection();
  }, [
    addManyProductsToCart,
    freeGiftProducts,
    selectedGift,
    giftInCart,
    closeGiftSelection,
    trackAddFreeGift,
  ]);

  return (
    <div className="fixed flex flex-col top-0 z-[1111] bg-white h-full w-full">
      <div className="flex justify-between items-center px-[15px] py-[17px] border-b border-dark-grey desktop:p-[20px]">
        <h3 className="typo-h2-bold">Choose Your Gift</h3>
        <button onClick={closeGiftSelection} type="button">
          <CloseIcon fill="black" width="15" height="15" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-x-[15px] gap-y-[30px] py-[20px] px-[15px] desktop:px-[20px] desktop:gap-x-[20px] overflow-y-scroll pb-[85px]">
        {giftProductsRandomized?.current?.map((product) => (
          <FreeGiftProduct
            product={product}
            key={product.handle}
            selectedGift={selectedGift}
            setSelectedGift={setSelectedGift}
            isAvailable={
              freeGiftProductsInventory.find((p) => p.handle === product.handle)?.availableForSale
            }
          />
        ))}
      </div>
      <div className="fixed inset-x-0 bg-white bottom-0 left-0 right-0 mt-auto p-[15px] desktop:p-[20px] shadow-[0_2px_16px_0_rgba(0,0,0,0.25)]">
        <ALButtonV2
          variant="primary"
          size="large"
          fullWidth
          onClick={handleConfirmationClick}
          disabled={!selectedGift}
        >
          Confirm Selection
        </ALButtonV2>
      </div>
    </div>
  );
}

export default FreeGiftSelection;
